import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Layout from "../components/Layout";
import { useAuth } from "../lib/auth";
import { api, fileUrl } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { Upload, X, Loader2 } from "lucide-react";

const DAYS = [
  ["mon", "Mon"], ["tue", "Tue"], ["wed", "Wed"], ["thu", "Thu"], ["fri", "Fri"], ["sat", "Sat"], ["sun", "Sun"],
];

export default function SubmitVenue() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const [searchParams] = useSearchParams();
  const prefillFree = searchParams.get("free") === "1";
  const isEdit = Boolean(editId);

  const [form, setForm] = useState({
    name: "", category: "restaurant", description: "", address: "",
    location: "Airlie Beach", phone: "", website: "", email: "",
    instagram: "", facebook: "",
    price_range: "$$", tagsInput: "",
    extra_categories: [],
    is_free: prefillFree,
    google_photos_enabled: true,
    google_rating_enabled: true,
    google_reviews_enabled: true,
  });
  const [hours, setHours] = useState({ mon: "", tue: "", wed: "", thu: "", fri: "", sat: "", sun: "" });
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loadingVenue, setLoadingVenue] = useState(isEdit);

  React.useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [user, loading, navigate]);

  // Load venue for editing
  useEffect(() => {
    if (!isEdit) return;
    api.get(`/venues/${editId}`).then((r) => {
      const v = r.data;
      setForm({
        name: v.name || "",
        category: v.category || "restaurant",
        description: v.description || "",
        address: v.address || "",
        location: v.location || "Airlie Beach",
        phone: v.phone || "",
        website: v.website || "",
        email: v.email || "",
        instagram: v.instagram || "",
        facebook: v.facebook || "",
        price_range: v.price_range || "$$",
        tagsInput: (v.tags || []).join(", "),
        extra_categories: v.extra_categories || [],
        is_free: v.is_free === true,
        google_photos_enabled: v.google_photos_enabled !== false,
        google_rating_enabled: v.google_rating_enabled !== false,
        google_reviews_enabled: v.google_reviews_enabled !== false,
      });
      setHours(v.hours || { mon: "", tue: "", wed: "", thu: "", fri: "", sat: "", sun: "" });
      setImages(v.images || []);
    }).catch(() => toast.error("Could not load venue")).finally(() => setLoadingVenue(false));
  }, [isEdit, editId]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = `${process.env.REACT_APP_BACKEND_URL}${r.data.url}`;
      setImages((s) => [...s, url]);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const tags = form.tagsInput.split(",").map((t) => t.trim()).filter(Boolean);
      const normHandle = (raw, prefix) => {
        if (!raw) return null;
        const t = raw.trim();
        if (!t) return null;
        if (/^https?:\/\//i.test(t)) return t;
        const handle = t.replace(/^@/, "").replace(/\/+$/, "");
        return `${prefix}${handle}`;
      };
      const body = {
        name: form.name,
        category: form.category,
        extra_categories: form.extra_categories.filter((c) => c !== form.category),
        description: form.description,
        address: form.address,
        location: form.location,
        phone: form.phone,
        website: form.website,
        email: form.email,
        instagram: normHandle(form.instagram, "https://instagram.com/"),
        facebook: normHandle(form.facebook, "https://facebook.com/"),
        price_range: form.price_range,
        is_free: form.is_free,
        tags,
        images,
        hours,
        google_photos_enabled: form.google_photos_enabled,
        google_rating_enabled: form.google_rating_enabled,
        google_reviews_enabled: form.google_reviews_enabled,
      };
      if (isEdit) {
        await api.put(`/venues/${editId}`, body);
        toast.success("Venue updated");
      } else {
        await api.post("/venues", body);
        toast.success("Venue submitted! Awaiting approval.");
      }
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (!user || loadingVenue) return <Layout><div className="py-20 text-center text-neutral-500">Loading...</div></Layout>;

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-6 py-12">
        <h1 className="font-heading text-4xl text-navy tracking-tight mb-2" data-testid="submit-title">
          {isEdit ? "Edit your venue" : "Submit a venue"}
        </h1>
        <p className="text-neutral-500 mb-8">
          {isEdit ? "Update details below. Changes are saved instantly." : "Free to list. Our editorial team approves within 48h."}
        </p>

        <form onSubmit={submit} className="bg-white rounded-2xl border border-black/5 p-8 space-y-6">
          <div>
            <label className="text-sm font-medium text-navy mb-1.5 block">Venue name</label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="submit-name" />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-navy mb-1.5 block">Category</label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="submit-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="restaurant">Restaurant</SelectItem>
                  <SelectItem value="bar">Bar</SelectItem>
                  <SelectItem value="cafe">Café</SelectItem>
                  <SelectItem value="activity">Activity</SelectItem>
                  <SelectItem value="nightlife">Live Music & Nightlife</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-navy mb-1.5 block">Price range</label>
              <Select value={form.price_range} onValueChange={(v) => setForm({ ...form, price_range: v })}>
                <SelectTrigger data-testid="submit-price"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="$">$ (Cheap)</SelectItem>
                  <SelectItem value="$$">$$ (Moderate)</SelectItem>
                  <SelectItem value="$$$">$$$ (Upscale)</SelectItem>
                  <SelectItem value="$$$$">$$$$ (Luxury)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-navy mb-1.5 block">Also show under (optional)</label>
            <p className="text-xs text-neutral-500 mb-2">Tick any extra categories your venue also belongs to. E.g. a pub might live under both <em>Nightlife</em> and <em>Drink</em>.</p>
            <div className="flex flex-wrap gap-2" data-testid="extra-categories">
              {[
                { slug: "restaurant", label: "Restaurant" },
                { slug: "bar", label: "Drink" },
                { slug: "cafe", label: "Café" },
                { slug: "activity", label: "Activities" },
                { slug: "nightlife", label: "Nightlife" },
              ].filter((c) => c.slug !== form.category).map((c) => {
                const checked = form.extra_categories.includes(c.slug);
                return (
                  <button
                    key={c.slug}
                    type="button"
                    onClick={() => setForm({
                      ...form,
                      extra_categories: checked
                        ? form.extra_categories.filter((x) => x !== c.slug)
                        : [...form.extra_categories, c.slug],
                    })}
                    className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${checked ? "bg-navy text-white border-navy" : "bg-white text-neutral-700 border-black/10 hover:border-navy"}`}
                    data-testid={`extra-cat-${c.slug}`}
                  >
                    {checked ? "✓ " : "+ "}{c.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-navy mb-1.5 block">Description</label>
            <Textarea rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="submit-description" />
          </div>

          <div>
            <label className="text-sm font-medium text-navy mb-1.5 block">Tags (comma separated)</label>
            <Input placeholder="seafood, waterfront, romantic" value={form.tagsInput} onChange={(e) => setForm({ ...form, tagsInput: e.target.value })} data-testid="submit-tags" />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-navy mb-1.5 block">Address</label>
              <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="submit-address" />
            </div>
            <div>
              <label className="text-sm font-medium text-navy mb-1.5 block">Location</label>
              <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="submit-location" />
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-medium text-navy mb-1.5 block">Phone</label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="submit-phone" />
            </div>
            <div>
              <label className="text-sm font-medium text-navy mb-1.5 block">Website</label>
              <Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} data-testid="submit-website" />
            </div>
            <div>
              <label className="text-sm font-medium text-navy mb-1.5 block">Email</label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="submit-email" />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-navy mb-1.5 block">Instagram</label>
              <Input
                placeholder="@yourhandle or full URL"
                value={form.instagram}
                onChange={(e) => setForm({ ...form, instagram: e.target.value })}
                data-testid="submit-instagram"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-navy mb-1.5 block">Facebook</label>
              <Input
                placeholder="page name or full URL"
                value={form.facebook}
                onChange={(e) => setForm({ ...form, facebook: e.target.value })}
                data-testid="submit-facebook"
              />
            </div>
          </div>

          <label className="flex items-start justify-between gap-4 bg-sponsored/10 rounded-2xl p-4 cursor-pointer" data-testid="is-free-toggle">
            <div className="flex-1">
              <div className="text-sm font-medium text-navy">Free thing to do</div>
              <div className="text-xs text-neutral-500 mt-0.5">Toggle on if this is a free activity (lookout, beach, walking track, market) — it will show up under Activities instead of the paid Tours section.</div>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={form.is_free}
              onClick={() => setForm({ ...form, is_free: !form.is_free })}
              className={`relative shrink-0 w-11 h-6 rounded-full transition-colors ${form.is_free ? "bg-navy" : "bg-neutral-300"}`}
              data-testid="is-free-switch"
            >
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${form.is_free ? "translate-x-5" : ""}`} />
            </button>
          </label>

          <div className="bg-sand/20 rounded-2xl p-6 space-y-4" data-testid="google-toggles">
            <div>
              <h3 className="font-heading text-lg text-navy">Google Places integration</h3>
              <p className="text-xs text-neutral-500 mt-1">Control what we pull from your Google Business listing. Toggle off any you&apos;d rather manage yourself.</p>
            </div>
            {[
              { key: "google_photos_enabled",  label: "Show Google photos",  hint: "Uses your Google Business photos for cards and previews." },
              { key: "google_rating_enabled",  label: "Show Google star rating", hint: "Displays the ⭐ average + review count on your venue card." },
              { key: "google_reviews_enabled", label: "Show Google reviews",  hint: "Shows recent reviewer text + names in the venue detail page." },
            ].map((t) => (
              <label key={t.key} className="flex items-start justify-between gap-4 cursor-pointer" data-testid={`toggle-${t.key}`}>
                <div className="flex-1">
                  <div className="text-sm font-medium text-navy">{t.label}</div>
                  <div className="text-xs text-neutral-500 mt-0.5">{t.hint}</div>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={form[t.key]}
                  onClick={() => setForm({ ...form, [t.key]: !form[t.key] })}
                  className={`relative shrink-0 w-11 h-6 rounded-full transition-colors ${form[t.key] ? "bg-navy" : "bg-neutral-300"}`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${form[t.key] ? "translate-x-5" : ""}`} />
                </button>
              </label>
            ))}
          </div>

          <div>
            <label className="text-sm font-medium text-navy mb-1.5 block">Opening hours</label>
            <p className="text-xs text-neutral-500 mb-2">Format: 09:00-17:00 (leave empty for closed, or type &apos;24h&apos;)</p>
            <div className="grid grid-cols-2 md:grid-cols-7 gap-2">
              {DAYS.map(([k, label]) => (
                <div key={k}>
                  <div className="text-xs text-neutral-500 mb-1">{label}</div>
                  <Input
                    placeholder="09:00-17:00"
                    value={hours[k]}
                    onChange={(e) => setHours({ ...hours, [k]: e.target.value })}
                    data-testid={`hours-${k}`}
                  />
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-navy mb-1.5 block">Photos</label>
            <div className="flex flex-wrap gap-3 mb-3">
              {images.map((src, i) => (
                <div key={src || `upload-${i}`} className="relative w-24 h-24 rounded-lg overflow-hidden border border-black/5">
                  <img src={src} alt="" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => setImages(images.filter((_, idx) => idx !== i))}
                    className="absolute top-1 right-1 bg-white/90 rounded-full p-0.5 hover:bg-white">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
              <label className="w-24 h-24 rounded-lg border-2 border-dashed border-navy/20 hover:border-navy flex items-center justify-center cursor-pointer transition-colors" data-testid="upload-label">
                {uploading ? <Loader2 className="w-5 h-5 animate-spin text-navy" /> : <Upload className="w-5 h-5 text-navy" />}
                <input type="file" accept="image/*" onChange={upload} className="hidden" data-testid="upload-input" />
              </label>
            </div>
          </div>

          <Button type="submit" disabled={submitting} className="w-full rounded-full bg-navy hover:bg-navy/90" data-testid="submit-venue-submit">
            {submitting ? (isEdit ? "Saving..." : "Submitting...") : (isEdit ? "Save changes" : "Submit for review")}
          </Button>
        </form>
      </div>
    </Layout>
  );
}
